import React, { useState } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import {
  Modal,
  Box,
  TextField,
  Button,
  Typography,
  FormControl,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { sendGTMEvent } from '@next/third-parties/google';
import { usePathname } from 'next/navigation';
import { useFormik } from 'formik';
import { colorVariables } from '@/constants';
import {
  initialValues,
  inputProps,
  STEPS,
  validationSchema,
} from './constants';
import CheckCircleIcon from '../CheckCircleIcon';
import CustomStepper from '../CustomStepper';
import apiClient from '@/apiClient';
import { useSnackbar } from '@/hooks/useSnackbar';
import { Vendor } from '@/lib/types/interfaces/Vendor';

interface FormModalProps {
  open: boolean;
  onClose: () => void;
  service: Vendor;
}

const LeadForm: React.FC<FormModalProps> = ({ open, onClose, service }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const {
    open: snackbarOpen,
    setOpen: setSnackbarOpen,
    initialSnackbarState,
  } = useSnackbar();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const isLargerThan680 = useMediaQuery('(min-height:680px)');

  const pathname = usePathname();

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async ({ ...values }) => {
      try {
        if (!executeRecaptcha) {
          return;
        }

        const recaptcha = await executeRecaptcha('lead_form_submit');

        const response: any = await apiClient.post('/leads/add', {
          cache: 'no-cache',
          body: JSON.stringify({
            ...values,
            listingId: service.id,
            recaptcha,
          }),
        });

        if (response?.errors || response?.error) {
          setSnackbarOpen({
            status: 'error',
            isOpen: true,
            message: 'Something went wrong, please try again',
          });
          return;
        }

        setCurrentStep(3);
        sendGTMEvent({
          event: 'successful_lead_form_submit',
          service_provider_name: service.business_name,
          url: pathname,
          ...values,
        });
      } catch (error) {
        setSnackbarOpen({
          status: 'error',
          isOpen: true,
          message: 'Something went wrong, please try again',
        });
        console.error(error);
      }
    },
  });

  const handleCloseModal = () => {
    onClose();
    setCurrentStep(0);
    formik.resetForm();
  };

  const handleNext = async () => {
    const errors = await formik.validateForm();

    if (currentStep === 0 && errors.jobDetails) {
      formik.handleSubmit();
      return;
    }

    if (currentStep === 1 && errors.timeframe) {
      formik.handleSubmit();
      return;
    }

    if (currentStep === 2) {
      formik.handleSubmit();
      return;
    }

    setCurrentStep((prev) => prev + 1);
    formik.setTouched({});
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSnackbarClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }

    setSnackbarOpen({ ...initialSnackbarState, isOpen: false });
  };

  return (
    <>
      <Modal
        open={open}
        aria-labelledby='modal-title'
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'white',
            boxShadow: 24,
            borderRadius: 0,
            width: 632,
            minHeight: isLargerThan680 ? 632 : 'calc(100dvh - 48px)',
            height: 'max-content',
            maxHeight: 'calc(100dvh - 48px)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              right: '24px',
              top: '24px',
              zIndex: 1,
              cursor: 'pointer',
              maxWidth: '24px',
              maxHeight: '24px',
            }}
            onClick={handleCloseModal}
          >
            <CloseIcon sx={{ color: colorVariables.textDescription }} />
          </Box>
          <Box
            sx={{ paddingY: 3, flexGrow: 1, overflow: 'auto' }}
            display='flex'
            flexDirection='column'
          >
            <Box paddingX={8}>
              <CustomStepper
                steps={STEPS.slice(0, 3)}
                activeStep={currentStep}
              />
            </Box>

            <Box
              mt={4}
              sx={{ overflow: 'auto' }}
              paddingX={4}
            >
              {currentStep === 0 && (
                <>
                  <Typography
                    variant='h6'
                    fontWeight={600}
                    fontSize='20px'
                    lineHeight='32px'
                    letterSpacing='0.15px'
                    color='#071307'
                  >
                    Add jobs specifics
                  </Typography>
                  <Typography
                    mt={1}
                    fontWeight={400}
                    fontSize='16px'
                    lineHeight='24px'
                    letterSpacing='0.15px'
                    color={colorVariables.textDescription}
                  >
                    Please describe the job specifics for the provider. The more
                    detail, the better!
                  </Typography>
                  <FormControl
                    fullWidth
                    sx={{ marginTop: 1 }}
                  >
                    <TextField
                      id='jobDetails'
                      name='jobDetails'
                      label='I need help with…'
                      fullWidth
                      margin='normal'
                      multiline
                      minRows={3}
                      variant='outlined'
                      value={formik.values.jobDetails}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.jobDetails
                        && Boolean(formik.errors.jobDetails)
                      }
                      helperText={
                        formik.touched.jobDetails && formik.errors.jobDetails
                      }
                    />
                  </FormControl>
                </>
              )}

              {currentStep === 1 && (
                <>
                  <Typography
                    variant='h6'
                    fontWeight={600}
                    fontSize='20px'
                    lineHeight='32px'
                    letterSpacing='0.15px'
                    color='#071307'
                  >
                    What is your timeframe?
                  </Typography>
                  <Typography
                    mt={1}
                    fontWeight={400}
                    fontSize='16px'
                    lineHeight='24px'
                    letterSpacing='0.15px'
                    color={colorVariables.textDescription}
                  >
                    Please briefly describe your timeframe for this project.
                  </Typography>
                  <FormControl
                    fullWidth
                    sx={{ marginTop: 1 }}
                  >
                    <TextField
                      id='timeframe'
                      name='timeframe'
                      label='My timeframe for this project is...'
                      fullWidth
                      margin='normal'
                      multiline
                      minRows={3}
                      variant='outlined'
                      value={formik.values.timeframe}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.timeframe
                        && Boolean(formik.errors.timeframe)
                      }
                      helperText={
                        formik.touched.timeframe && formik.errors.timeframe
                      }
                    />
                  </FormControl>
                </>
              )}

              {currentStep === 2 && (
                <>
                  <Typography
                    variant='h6'
                    fontWeight={600}
                    fontSize='20px'
                    lineHeight='32px'
                    letterSpacing='0.15px'
                    color='#071307'
                  >
                    Where should the service provider contact you?
                  </Typography>
                  <FormControl
                    fullWidth
                    sx={{ marginTop: 1, overflow: 'auto' }}
                  >
                    <TextField
                      id='userName'
                      name='userName'
                      label='Name*'
                      fullWidth
                      margin='normal'
                      variant='outlined'
                      value={formik.values.userName}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.userName
                        && Boolean(formik.errors.userName)
                      }
                      helperText={
                        formik.touched.userName && formik.errors.userName
                      }
                      sx={{ mt: 2 }}
                      inputProps={inputProps}
                    />
                    <TextField
                      id='email'
                      name='email'
                      label='Email*'
                      fullWidth
                      margin='normal'
                      variant='outlined'
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.email && Boolean(formik.errors.email)
                      }
                      helperText={formik.touched.email && formik.errors.email}
                      sx={{ mt: 2 }}
                      inputProps={inputProps}
                    />
                    <TextField
                      id='phoneNumber'
                      name='phoneNumber'
                      label='Phone Number*'
                      fullWidth
                      margin='normal'
                      variant='outlined'
                      value={formik.values.phoneNumber}
                      onChange={formik.handleChange}
                      error={
                        formik.touched.phoneNumber
                        && Boolean(formik.errors.phoneNumber)
                      }
                      helperText={
                        formik.touched.phoneNumber && formik.errors.phoneNumber
                      }
                      sx={{ mt: 2 }}
                      inputProps={inputProps}
                    />
                    <FormControlLabel
                      control={(
                        <Checkbox
                          id='isAllowed'
                          name='isAllowed'
                          checked={formik.values.isAllowed}
                          onChange={formik.handleChange}
                        />
                      )}
                      label={(
                        <Typography
                          sx={{
                            color: '#071307',
                            fontSize: '16px',
                            fontWeight: '400',
                            lineHeight: '150%',
                            letterSpacing: '0.15px',
                          }}
                        >
                          Allow PropJump to request quotes from similar
                          providers
                        </Typography>
                      )}
                      sx={{
                        mt: 2,
                        ml: '0px',
                      }}
                    />
                  </FormControl>
                </>
              )}

              {currentStep === 3 && (
                <Box
                  display='flex'
                  flexDirection='column'
                  justifyContent='center'
                  alignItems='center'
                  mt='117px'
                >
                  <Box
                    width={124}
                    height={124}
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                  >
                    <CheckCircleIcon
                      color={colorVariables.primaryColor}
                      width={98}
                      height={98}
                    />
                  </Box>
                  <Typography
                    variant='h6'
                    fontWeight={600}
                    fontSize='20px'
                    lineHeight='32px'
                    letterSpacing='0.15px'
                    color='#071307'
                    mt={3}
                    textAlign='center'
                  >
                    Your request has been submitted successfully!
                  </Typography>
                  <Typography
                    mt={1}
                    fontWeight={400}
                    fontSize='16px'
                    lineHeight='24px'
                    letterSpacing='0.15px'
                    color={colorVariables.textDescription}
                    textAlign='center'
                  >
                    We will review your details and get back to you as soon as
                    possible.
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {currentStep !== 3 && (
            <>
              {currentStep === 2 && (
                <Typography
                  fontWeight={400}
                  fontSize='12px'
                  lineHeight='20px'
                  letterSpacing='0.4px'
                  color={colorVariables.textDescription}
                  textAlign='center'
                  mb={2}
                >
                  This site is protected by reCAPTCHA and the
                  {' '}
                  <a
                    href='https://policies.google.com/privacy'
                    style={{ color: colorVariables.primaryColor }}
                    target='_blank'
                    rel='noreferrer nofollow'
                  >
                    Google Privacy Policy
                  </a>
                  {' '}
                  and
                  {' '}
                  <a
                    href='https://policies.google.com/terms'
                    style={{ color: colorVariables.primaryColor }}
                    target='_blank'
                    rel='noreferrer nofollow'
                  >
                    Terms of Service
                  </a>
                  {' '}
                  apply.
                </Typography>
              )}
              <Box
                display='flex'
                justifyContent={
                  currentStep === 0 ? 'flex-end' : 'space-between'
                }
                sx={{
                  borderTop: `1px solid ${colorVariables.separatorColor}`,
                  paddingX: 4,
                  paddingY: 3,
                }}
              >
                {currentStep !== 0 && (
                  <Button
                    disabled={currentStep === 0}
                    onClick={handleBack}
                    variant='outlined'
                    sx={{
                      minHeight: '42px',
                      width: '108px',
                    }}
                  >
                    Back
                  </Button>
                )}
                <Button
                  onClick={handleNext}
                  variant='contained'
                  color='success'
                  sx={{
                    minHeight: '42px',
                    width: '108px',
                  }}
                  disabled={formik.isSubmitting}
                >
                  {currentStep === 2 ? 'Submit' : 'Next'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>
      <Snackbar
        open={snackbarOpen.isOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <div>
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarOpen.status}
            variant='filled'
            sx={{ width: '100%' }}
          >
            {snackbarOpen.message}
          </Alert>
        </div>
      </Snackbar>
    </>
  );
};

export default LeadForm;
